// derivePersonEmployment.ts — leitet aus den Firma↔Person-Verbindungen einer
// PERSON die anzuzeigende „Anstellung/Beruf"-Information ab: Arbeitgeber-Kontakt
// (die verbundene Firma), Verantwortlichkeit(en)/Rolle, intern/extern sowie
// Branche & Tätigkeit der Firma. Rein (keine Seiteneffekte) → unit-testbar.
//
// Datenquelle sind die bereits geladenen Relationen des Kontakt-Details:
//   - relationsSrc: Person = ContactSrc → Gegenüber = contactDest
//   - relationsDest: Person = ContactDest → Gegenüber = contactSrc
// Firma-Gegenüber werden geladen als volle Contact-Entities, tragen also ihre
// Stammfelder jobIndustry (Branche) / jobOccupation (Tätigkeit).

import { LABEL_GF_GESELLSCHAFTER } from "./gfGesellschafter";

export interface EmpParty {
  id?: number;
  displayName?: string;
  lastname?: string;
  firstName?: string;
  typ?: string;
  type?: { type?: string } | null;
  jobIndustry?: string | null;
  jobOccupation?: string | null;
  // Fallbacks, falls die Serialisierung Spaltennamen/Alias liefert.
  industry?: string | null;
  occupation?: string | null;
  branche?: string | null;
  taetigkeit?: string | null;
}

export interface EmpRelation {
  id?: number;
  relationType?: { contactRelationType?: string } | null;
  function?: string | null;
  isExtern?: boolean;
  contactSrc?: EmpParty | null;
  contactDest?: EmpParty | null;
  externalCompany?: EmpParty | null;
  /** Der bAV/bKV-Haken der Beziehung: Arbeitet dieser Mensch im Unternehmen? */
  istMenschImUnternehmen?: boolean;
  /** DIESE Firma ist der Arbeitgeber — ausdrücklich markiert, je Person höchstens einmal. */
  istHauptarbeitgeber?: boolean;
  /** Die Person ist aus dieser Verbindung ausgeschieden (früherer Arbeitgeber). */
  ausgeschieden?: boolean | null;
}

/** Eine (je Firma gebündelte) Anstellungs-/Verbindungszeile für die Beruf-Sektion. */
export interface EmploymentCompany {
  companyId?: number;
  companyName: string;
  /** Verantwortlichkeit(en)/Rolle(n), dedupliziert. */
  roles: string[];
  /** true = „Mensch fürs Unternehmen" extern (kein internes Angestelltenverhältnis). */
  isExtern: boolean;
  /** Mindestens eine Beziehung zu dieser Firma trägt den bAV/bKV-Haken. */
  istMenschImUnternehmen: boolean;
  /** Diese Firma ist ausdrücklich als Arbeitgeber markiert. */
  istHauptarbeitgeber: boolean;
  /** Branche der verbundenen Firma (Contact.jobIndustry). */
  branche?: string;
  /** Tätigkeit der verbundenen Firma (Contact.jobOccupation). */
  taetigkeit?: string;
  /** Bei extern: Name der externen Arbeitgeber-Firma, falls hinterlegt. */
  externalCompanyName?: string;
}

function partyName(p: EmpParty | null | undefined): string {
  if (!p) return "—";
  const composed = `${p.lastname ?? ""}${p.firstName ? ", " + p.firstName : ""}`
    .replace(/^,\s*|,\s*$/g, "")
    .trim();
  return p.displayName || composed || "—";
}

function isFirma(p: EmpParty | null | undefined): boolean {
  return (p?.type?.type || p?.typ || "") === "Firma";
}

function firstNonEmpty(...vals: Array<string | null | undefined>): string | undefined {
  for (const v of vals) {
    const t = (v ?? "").trim();
    if (t) return t;
  }
  return undefined;
}

/**
 * Label für „Geschäftsführer" UND „Gesellschafter" in Personalunion.
 *
 * Die eine Schreibweise steht seit 08/2026 in `utils/gfGesellschafter.ts` —
 * vorher lief hier „Geschäftsführer und Gesellschafter" neben
 * „Geschäftsführender Gesellschafter" (Organigramm, Beruf-Ableitung) her.
 */
export const GFG_COMBINED_LABEL = LABEL_GF_GESELLSCHAFTER;

/**
 * Sprechendes Rollen-/Verantwortlichkeits-Label aus (relationType, function).
 * Arbeitet bewusst auf den beiden Rohwerten statt auf einer Relation-Form,
 * damit ContactConnections (UI-Typ `Rel`) dieselbe Logik nutzen kann.
 */
export function roleLabelFrom(relationType?: string | null, functionValue?: string | null): string {
  const relType = (relationType || "").trim();
  const func = (functionValue || "").trim();
  // Geschäftsführender Gesellschafter (function='geschäftsführend' ist ein
  // interner Flag, kein anzeigbares Label).
  if (relType === "Gesellschafter" && func === "geschäftsführend") {
    return GFG_COMBINED_LABEL;
  }
  // „Mensch fürs Unternehmen": die konkrete Verantwortlichkeit steht in function.
  if (relType === "Mensch fürs Unternehmen") return func || relType;
  return relType || func;
}

/** Sprechendes Rollen-/Verantwortlichkeits-Label einer einzelnen Relation. */
function roleLabel(rel: EmpRelation): string {
  return roleLabelFrom(rel.relationType?.contactRelationType, rel.function);
}

/** Steuert, welche Verbindungen in die Bündelung einfließen. */
export interface EmploymentOptions {
  /**
   * „Mensch im Unternehmen" mitnehmen (Default: nein).
   *
   * Für die Status-Pille und die Firmenzuordnung der Kommunikation zählt nur,
   * wer eine echte Verantwortlichkeit trägt — ein reiner Angestellter ist dort
   * keine Verbindung, die man anzeigen will. Für den ARBEITGEBER ist genau er
   * aber der Regelfall, deshalb dieser Schalter statt einer zweiten Ableitung.
   */
  inklusiveMenschImUnternehmen?: boolean;
  /**
   * Ausgeschiedene Verbindungen auslassen (Default: nein). Für die Auswahl
   * „Unternehmen“ an geschäftlichen Kommunikationswegen: frühere Arbeitgeber
   * stehen dort nicht zur Wahl (User 02.10.2026).
   */
  ohneAusgeschiedene?: boolean;
}

/**
 * Bündelt alle Firma-Verbindungen der Person je Firma zu einer Anzeigezeile.
 * „Mensch im Unternehmen" (Status, keine echte Verantwortlichkeit) wird
 * ausgelassen — außer der Aufrufer verlangt es ausdrücklich (siehe Optionen).
 */
export function derivePersonEmployment(
  relationsSrc?: EmpRelation[] | null,
  relationsDest?: EmpRelation[] | null,
  options?: EmploymentOptions,
): EmploymentCompany[] {
  const pairs: Array<{ rel: EmpRelation; other: EmpParty | null | undefined }> = [
    ...(relationsSrc ?? []).map((rel) => ({ rel, other: rel.contactDest })),
    ...(relationsDest ?? []).map((rel) => ({ rel, other: rel.contactSrc })),
  ];

  const byCompany = new Map<string, EmploymentCompany>();
  const order: string[] = [];

  for (const { rel, other } of pairs) {
    if (!isFirma(other)) continue;
    const relType = (rel.relationType?.contactRelationType || "").trim();
    if (relType === "Mensch im Unternehmen" && !options?.inklusiveMenschImUnternehmen) continue;
    if (options?.ohneAusgeschiedene && rel.ausgeschieden === true) continue;

    const key = other?.id != null ? "id:" + other.id : "nm:" + partyName(other);
    let row = byCompany.get(key);
    if (!row) {
      row = {
        companyId: other?.id,
        companyName: partyName(other),
        roles: [],
        isExtern: false,
        istMenschImUnternehmen: false,
        istHauptarbeitgeber: false,
        branche: firstNonEmpty(other?.jobIndustry, other?.industry, other?.branche),
        taetigkeit: firstNonEmpty(other?.jobOccupation, other?.occupation, other?.taetigkeit),
      };
      byCompany.set(key, row);
      order.push(key);
    }
    const label = roleLabel(rel);
    if (label && !row.roles.includes(label)) row.roles.push(label);
    if (rel.isExtern) row.isExtern = true;
    if (rel.istMenschImUnternehmen) row.istMenschImUnternehmen = true;
    if (rel.istHauptarbeitgeber) row.istHauptarbeitgeber = true;
    const extName = firstNonEmpty(partyName(rel.externalCompany) === "—" ? undefined : partyName(rel.externalCompany));
    if (extName && !row.externalCompanyName) row.externalCompanyName = extName;
  }

  return order.map((k) => byCompany.get(k)!);
}
